import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Grid, Card, Toolbar, CardContent, Dialog, DialogContent, DialogActions, Typography, Button, LinearProgress } from '@mui/material';
import Config from '../../config';
import axios from 'axios';
import EnhancedTable from '../../components/table';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import DownloadButton from '../../components/download_button';
import TextField from '../../components/text_field';
import Auth from '../../auth';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/agreements.tsx/user_profile.tsx/product_profile.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

type PaymentTerm = {
    Id?: number | string;
    PartnerCoId?: number | string | null;
    AccountNumber?: number | string;
    PartnerName?: string;
    TermsDescription?: string;
    PayInNumberOfDays?: number;
    DiscountInNumberOfDays?: number;
    DiscountPercent?: number;
    [key: string]: any;
};

type DefaultPaymentTermsProps = {
    // Never actually supplied by the current route wiring (App.js renders
    // SupplierPaymentTerms without an addressType prop), so `e.AddressType`
    // below is always undefined and dropped by JSON.stringify -- a harmless
    // pre-existing no-op, not something this migration should change.
    setTitle?: (title: string, includeInRecents?: boolean) => void;
    addressType?: string;
};

const DefaultPaymentTerms = (props: DefaultPaymentTermsProps) => {
    const { setTitle, addressType } = props;

    const [showEdit, setShowEdit] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [defaultTerm, setDefaultTerm] = useState<PaymentTerm | undefined>(undefined);
    const [disableEdit, setDisableEdit] = useState<boolean | undefined>(undefined);
    const [refresh, setRefresh] = useState<boolean | undefined>(undefined);
    const [editTerm, setEditTerm] = useState<PaymentTerm | null | undefined>(undefined);
    const [disableConfirm, setDisableConfirm] = useState<boolean | undefined>(undefined);

    const loadDefaultTerm = useCallback(async () => {
        var terms = (await axios.get(Config.api + `/odata/Company/PaymentTerms`)).data.value;
        var term = terms.find((r: PaymentTerm) => r.PartnerCoId === null);
        term = term || {};
        setDefaultTerm(term);
    }, []);

    useEffect(() => {
        if(setTitle) {
            setTitle('Payment Terms', true);
        }
        loadDefaultTerm();
        // Mount-only effect (mirrors componentDidMount); loadDefaultTerm is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTableData = async () => {
        var partners = (await axios.get(Config.api + `/api/v1/partners`)).data.Body.Partners;
        var terms = (await axios.get(Config.api + `/odata/Company/PaymentTerms`)).data.value;
        return partners.map((row: any) => {
            var term = terms.find((r: PaymentTerm) => r.PartnerCoId === row.Id);
            term = term || {};
            var ret: PaymentTerm = {PartnerName: row.CompanyName, AccountNumber: row.Id};
            Object.assign(ret, term);
            return ret;
        });
    };

    const saveDefaultTerm = async (e: PaymentTerm) => {
        setDisableEdit(true);
        e.AddressType = addressType;
        await axios.put(Config.api + '/odata/Company/PaymentTerms', e);
        if(e.PartnerCoId === null || e.PartnerCoId === undefined){
            await loadDefaultTerm();
        }else{
            setRefresh(prev => !prev);
            setShowEdit(false);
        }
    };

    const loadTerm = async (id: number | string, partner: number | string) => {
        setShowEdit(true);
        setEditTerm(null);
        setDisableEdit(false);
        if(id || id === 0){
            var addr = (await axios.get(Config.api + `/odata/Company/PaymentTerms?$filter=Id eq ${id}`)).data.value[0];
            setEditTerm(addr);
        }else{
            setEditTerm({PartnerCoId: partner});
        }
    };

    const deleteTerm = async () => {
        setDisableConfirm(true);
        await axios.delete(Config.api + `/odata/Company/PaymentTerms(${editTerm.Id})`);
        setShowConfirm(false);
        setRefresh(prev => !prev);
    };

    let canEdit = Auth.hasPermission('settings/manage');
    var config = {
        getData: getTableData,
        columns: [
          { id: "AccountNumber", filterable: true, sortable: true, label: 'Account Number', width: '13em' },
          { id: 'PartnerName', filterable: true, sortable: true, label: 'Company Name', width: '25em' },
          { id: 'TermsDescription', sortable: true, label: 'Description' },
          { id: 'PayInNumberOfDays', sortable: true, label: 'Net Days Due', width: '15em' },
          { id: 'DiscountInNumberOfDays', sortable: true, label: 'Net Discount Days Due', width: '18em' },
          { id: 'DiscountPercent', sortable: true, label: 'Discount Percentage', width: '15em' },
          { command: 'commands', stopPropagation: 'true', width: '15em', template: (value: any, row: PaymentTerm) => <Fragment>
              <Button variant='outlined' style={{marginRight: '1rem'}} size='small' disabled={!canEdit} onClick={() => loadTerm(row.Id, row.AccountNumber)}>Edit</Button>
              <Button variant='outlined' size='small' disabled={!canEdit} onClick={() => { setEditTerm(row); setShowConfirm(true); setDisableConfirm(false); }}>Delete</Button>
          </Fragment>}
      ],
      order: 'asc',
      orderBy: 'PartnerName',
      keyField: 'AccountNumber',
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
      refresh: refresh,
      title: `Partner Specific Payment Terms`
    };
    return (
        <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{`Default Payment Terms`}</Toolbar>
                        <CardContent>
                            <Form onSubmit={saveDefaultTerm} initialValues={defaultTerm} render={({ handleSubmit, pristine, invalid, values }: any) => {
                                  return (
                                      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', maxWidth: '900px'}}>
                                      <div style={{display: 'flex'}}>
                                        <Field style={{flex: 1}} component={TextField} disabled={!canEdit} label='Terms Description' name='TermsDescription'/>
                                      </div>
                                      <div style={{display: 'flex', marginBottom: '.5em'}}>
                                        <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={!canEdit} type='number' label='Net Days Due' name='PayInNumberOfDays'/>
                                        <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={!canEdit} type='number' label='Net Discount Days Due' name='DiscountInNumberOfDays'/>
                                        <Field style={{flex: 1}} component={TextField} disabled={!canEdit} type='number' label='Discount Percentage' name='DiscountPercent'/>
                                      </div>
                                       <div style={{textAlign: 'left', marginBottom: '.5em'}}>
                                        <DownloadButton onClick={handleSubmit} disabled={pristine || !canEdit} variant='contained' color='primary'>Save</DownloadButton>
                                       </div>
                                      </form>
                                  );}}/>
                        </CardContent>
                </Card>
            </Grid>
            <Grid size={{ md: 12 }}>
                <EnhancedTable config={config}/>
            </Grid>
            <Dialog
                maxWidth="sm"
                aria-labelledby="confirmation-dialog-title"
                open={showConfirm}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (disableConfirm) return;
                  }
                  setShowConfirm(false);
                }}>
                <Toolbar className='lbtoolbar'>Confirm Delete</Toolbar>
                <DialogContent>
                  <Typography>Are you sure you want to delete these payment terms?</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowConfirm(false)} disabled={disableConfirm} color="primary">
                    No
                  </Button>
                  <Button onClick={deleteTerm} disabled={disableConfirm} color="primary">
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
            <Dialog
                maxWidth="sm"
                open={showEdit}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (disableEdit) return;
                  }
                  setShowEdit(false);
                }}
                fullWidth={true}>
                {editTerm ?
                    <Form onSubmit={saveDefaultTerm}
                    initialValues={editTerm}
                    render={({ handleSubmit, pristine, invalid, values }: any) => (
                      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                      <Toolbar className='lbtoolbar'>Edit Payment Terms</Toolbar>
                    <DialogContent>
                    <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                        <div style={{display: 'flex'}}>
                          <Field style={{flex: 1}} component={TextField} label='Terms Description' name='TermsDescription'/>
                        </div>
                        <div style={{display: 'flex', marginBottom: '.5em'}}>
                          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} type='number' label='Net Days Due' name='PayInNumberOfDays'/>
                          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} type='number' label='Net Discount Days Due' name='DiscountInNumberOfDays'/>
                          <Field style={{flex: 1}} component={TextField} type='number' label='Discount Percentage' name='DiscountPercent'/>
                        </div>
                      </div>
                    </DialogContent>
                    <DialogActions>
                      <Button type='submit' disabled={pristine || invalid || disableEdit} color="primary">
                        Save
                      </Button>
                    </DialogActions>
                    </form>
                    )}/>
                     :  <DialogContent><LinearProgress style={{height: '2em'}} variant="indeterminate"/></DialogContent> }

              </Dialog>
        </Grid>
    );
};

export default DefaultPaymentTerms;
